import {
  Button,
  Flex,
  Grid,
  IconAddLine,
  IconTrashLine,
  InPlaceEdit,
  List,
  Text,
} from "@instructure/ui";
import { IconButton } from "@instructure/ui-buttons";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";

const EDITOR_MODE = "view";

export const ActionTypePanel = ({ onAssignmentTypesReload, external }) => {
  const showNotification = useNotification();
  const [actionTypes, setActionTypes] = useState([]);
  const [backupTypeName, setBackupTypeName] = useState({});
  const [actionTypeToRemove, setActionTypeToRemove] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const getActionType = external
      ? ExternalService.getExternalActionTypes
      : ActionService.getActionTypes;
    getActionType()
      .then((actionTypeList) => {
        actionTypeList.forEach(
          (actionType) => (actionType.nameEditorMode = EDITOR_MODE)
        );
        setActionTypes(actionTypeList);
        if (onAssignmentTypesReload) {
          onAssignmentTypesReload(actionTypeList);
        }
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("actions.actionTypes"),
          }),
          ERROR
        );
      })
      .finally(() => setLoading(false));
  }, [showNotification]);

  const removeActionType = async () => {
    if (!external) {
      await ActionService.deleteActionType(actionTypeToRemove.id)
        .then(() => {
          const index = actionTypes.indexOf(actionTypeToRemove);
          if (index > -1) {
            actionTypes.splice(index, 1);
          }
          setActionTypes([...actionTypes]);
          setActionTypeToRemove(null);
          if (onAssignmentTypesReload) {
            onAssignmentTypesReload(actionTypes);
          }
          showNotification(
            t("notifications.general.deleteSuccess", {
              obj: t("notifications.actionType.obj"),
            })
          );
        })
        .catch(() => {
          setActionTypeToRemove(null);
          showNotification(
            t("notifications.general.deleteError", {
              obj: t("notifications.actionType.obj"),
            }),
            ERROR
          );
        });
    }
  };

  const renderView = (value, weight, message, isName) => (
    <Text
      lineHeight="double"
      color={value ? "primary" : "secondary"}
      weight={value ? weight : "light"}
      size={value ? (isName ? "medium" : "small") : "medium"}
    >
      {value || message}
    </Text>
  );

  const handleChange = (index, propertyName, event) => {
    actionTypes[index][propertyName] = event.target.value;
    setActionTypes([...actionTypes]);
  };

  const renderEdit = (index, propertyName, { onBlur, editorRef }) => (
    <Text
      color="primary"
      size="medium"
      as="input"
      type="text"
      value={actionTypes[index][propertyName]}
      onChange={(event) => handleChange(index, propertyName, event)}
      aria-label=""
      onBlur={onBlur}
      elementRef={editorRef}
    />
  );

  const renderEditButton = (props, message) => {
    props.label = message;
    return InPlaceEdit.renderDefaultEditButton(props);
  };

  const handleChangeMode = (index, propertyName, mode) => {
    if (mode === EDITOR_MODE && !!actionTypes[index].name) {
      let promise;
      if (external) {
        promise = actionTypes[index].id
          ? ExternalService.updateExternalActionType(
              actionTypes[index].id,
              actionTypes[index].name
            )
          : ExternalService.createExternalActionType(actionTypes[index].name);
      } else {
        promise = actionTypes[index].id
          ? ActionService.updateActionType(
              actionTypes[index].id,
              actionTypes[index].name
            )
          : ActionService.createActionType(actionTypes[index].name);
      }
      promise
        .then((newActionType) => {
          actionTypes[index] = Object.assign(actionTypes[index], newActionType);
          setActionTypes([...actionTypes]);
          if (onAssignmentTypesReload) {
            onAssignmentTypesReload(actionTypes);
          }
        })
        .catch(() => {
          // revert to the old value
          actionTypes[index].name = backupTypeName[index];
          setActionTypes([...actionTypes]);
        })
        .finally(() => {
          delete backupTypeName[index];
          setBackupTypeName({ ...backupTypeName });
        });
    } else {
      backupTypeName[index] = actionTypes[index].name;
      setBackupTypeName({ ...backupTypeName });
    }
    actionTypes[index][propertyName] = mode;
    setActionTypes([...actionTypes]);
  };

  const handleActionButtonClick = () => {
    const actionType = { name: "", nameEditorMode: EDITOR_MODE };
    actionTypes.unshift(actionType);
    setActionTypes([...actionTypes]);
  };

  return (
    <>
      <Grid hAlign="end" vAlign="middle">
        <Grid.Row>
          <Grid.Col width="auto">
            <Button
              color="primary"
              onClick={handleActionButtonClick}
              renderIcon={IconAddLine}
              key={"attainment-add-button"}
              disabled={Object.keys(backupTypeName).length}
            >
              {t("actions.addActionType")}
            </Button>
          </Grid.Col>
        </Grid.Row>
      </Grid>
      <div style={{ marginTop: 15 }}>
        {loading ? (
          <Skeleton count={3} style={{ marginBottom: 10, height: 60 }} />
        ) : (
          <>
            <List margin="0" isUnstyled>
              {actionTypes &&
                actionTypes.map((type, index) => {
                  return (
                    <List.Item key={index}>
                      <div
                        style={{
                          backgroundColor: "#F2F8FA",
                          border: "1px solid #333",
                          borderRadius: "10px",
                          padding: "5px 15px",
                          marginBottom: 10,
                        }}
                      >
                        <Flex justifyItems="space-between" alignItems="center">
                          <Flex.Item shouldGrow shouldShrink>
                            <InPlaceEdit
                              renderViewer={() =>
                                renderView(
                                  type.name,
                                  "normal",
                                  t("actions.enterActionType"),
                                  true
                                )
                              }
                              renderEditor={(props) =>
                                renderEdit(index, "name", props)
                              }
                              renderEditButton={(props) =>
                                renderEditButton(
                                  props,
                                  t("actions.editActionType")
                                )
                              }
                              onChangeMode={(mode) =>
                                handleChangeMode(index, "nameEditorMode", mode)
                              }
                              mode={type.nameEditorMode}
                              value={type.name}
                              inline={true}
                            />
                          </Flex.Item>
                          {!external && (
                            <Flex.Item padding="0 0 0 medium">
                              <IconButton
                                shape="rectangle"
                                screenReaderLabel={t(
                                  "assignment.deleteTagScreenReaderLabel"
                                )}
                                onClick={() => setActionTypeToRemove(type)}
                              >
                                <IconTrashLine />
                              </IconButton>
                            </Flex.Item>
                          )}
                        </Flex>
                      </div>
                    </List.Item>
                  );
                })}
            </List>
            <DialogYesNo
              message={t("actionType.deleteActionType")}
              openStatus={!!actionTypeToRemove}
              onClickNo={() => setActionTypeToRemove(null)}
              onClickYes={removeActionType}
            />
          </>
        )}
      </div>
    </>
  );
};
